import { REMOVE_URL } from './CveActions';

// Removing a program from the drift report. The report's Update jobs come from the API's drift result; a removal is asked of the same route
// the Devices page uses (cma/softwareinstall/remove), for ONE exact copy: the name and version the machine reported, so an old copy can go
// while a newer one stays. The page sends the machine ids, the name and the version, never a script.

const list = (v) => (Array.isArray(v) ? v : []);

// What a finding can be removed as: a program that is installed (it has a version) and that the policy does not require. A required program is
// not offered: removing it would only make it show as missing.
const REMOVABLE_GAPS = ['outdated', 'unverified', 'oldcopy', 'forbidden-present'];
export const canRemove = (finding) => Boolean(finding?.installed) && finding.mode !== 'required' && REMOVABLE_GAPS.includes(finding.gap);

// Why a finding cannot be removed, for the checkbox's tooltip ('' when it can).
export const cannotRemoveBecause = (finding) => {
  if (canRemove(finding)) return '';
  if (finding?.mode === 'required') return 'The policy requires this program.';
  if (!finding?.installed) return 'It is not installed.';
  return 'This cannot be removed from here.';
};

// [{deviceIds, name, version}] with one request for each exact copy: machines that have the same name and version are asked together.
export const removalRequests = (items) => {
  const byCopy = new Map();
  list(items).forEach((item) => {
    const key = `${item.name}|${item.version}`;
    const request = byCopy.get(key) || { name: item.name, version: item.version, deviceIds: [] };
    list(item.deviceIds).forEach((id) => { if (!request.deviceIds.includes(id)) request.deviceIds.push(id); });
    byCopy.set(key, request);
  });
  return [...byCopy.values()];
};

// Asks the API to test (kind "check", a dry run) or do (kind "apply") each removal, one after another, and gathers the answers into one:
// { queued, refused, failed }, each entry {key: the machine's id, name: its name, id | error}. `name` of each answer is changed to the
// program's, so the page can say which program each job is for.
export const queueRemovals = async (request, account, kind, items) => {
  const all = { queued: [], refused: [], failed: [] };
  for (const copy of removalRequests(items)) {
    // eslint-disable-next-line no-await-in-loop
    const response = await request('POST', REMOVE_URL, { account, kind, deviceIds: copy.deviceIds, name: copy.name, version: copy.version, exact: true });
    const data = response?.data || {};
    ['queued', 'refused', 'failed'].forEach((field) => all[field].push(...list(data[field]).map((entry) => ({ ...entry, program: copy.name }))));
  }
  return all;
};

// The same answer by machine, as the By application view shows it: [{deviceId, computer, queued, refused, failed}].
export const byMachine = (answer) => {
  const machines = new Map();
  ['queued', 'refused', 'failed'].forEach((field) => list(answer?.[field]).forEach((entry) => {
    const machine = machines.get(entry.key) || { deviceId: entry.key, computer: entry.name, queued: [], refused: [], failed: [] };
    machine[field].push({ ...entry, name: entry.program || entry.name });
    machines.set(entry.key, machine);
  }));
  return [...machines.values()];
};

// A link to a page about the app (its description, homepage and license), built from its id. '' when the app has no id.
export const infoUrl = (wingetId) => (wingetId ? `https://winstall.app/apps/${encodeURIComponent(wingetId)}` : '');
